import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { CountryEnum, PhoneInputStore, showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { type I2FASetting } from 'entities/Auth'
import { AuthApi } from 'entities/Auth/api'
import { TwoFaModalStore } from 'entities/Auth/ui/TwoFaModal'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { UsersApi } from 'entities/Users'
import { type ISecurityFormFields } from 'pages/settings/pages/personal/pages/account/pages/security/store/type'
import { PhoneModal } from 'pages/settings/pages/personal/pages/account/pages/security/ui/PhoneModal/PhoneModal'
import { PhoneModalActions } from 'pages/settings/pages/personal/pages/account/pages/security/ui/PhoneModal/PhoneModalActions'
import { type AccountStore } from 'pages/settings/pages/personal/pages/account/store/accountStore'

export class SecurityStore {
  private _loading = false
  private _initialLoading = true
  private _modalId = nanoid()
  private _twoFAuthSettingsMap: Map<I2FASetting['type'], I2FASetting> = new Map()
  private _errorsMap = new Map<ISecurityFormFields, string>()
  private _number = ''
  private _phoneModalOnConfirm: (() => void) | null = null
  private readonly _phoneInputStore
  private readonly _twoFaModalStore: TwoFaModalStore

  constructor(private _accountStore: AccountStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._twoFaModalStore = new TwoFaModalStore(this._entities.authStore)
    this._twoFaModalStore.setChangeSecurityMode(true)
    this._number = this._accountStore.user.user_number || ''
    this._phoneInputStore = new PhoneInputStore()
  }

  init = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      this._phoneInputStore.setExcludeCountries(
        this._accountStore.profileStore.phoneExcludeCountries
      )

      await this.getTwoFAuthSettings()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
        this._initialLoading = false
      })
    }
  }

  reset = () => {
    this._loading = false
    this._initialLoading = true
    this._twoFAuthSettingsMap = new Map()
    this._phoneInputStore.reset()
    this._errorsMap.clear()
  }

  getTwoFAuthSettings = async () => {
    try {
      const { data } = await AuthApi.getTwoFAuthSettings()

      if (data) {
        runInAction(() => {
          data.forEach((item) => {
            this._twoFAuthSettingsMap.set(item.type, item)
          })
        })
      }
    } catch (e) {
      logger.error(e)
    }
  }

  toggleSetting = async (setting: I2FASetting['type'], value: boolean) => {
    try {
      const settingFromMap = this._twoFAuthSettingsMap.get(setting)

      if (typeof settingFromMap?.value === 'undefined') return
      if (setting === 'app') await this._twoFaModalStore.getAppQrCode()
      if (setting === 'sms' && !this._number) {
        this.openPhoneModal(() => {
          this.openTwoFaModal(setting, value)
        })

        return
      }

      this.openTwoFaModal(setting, value)
    } catch (e) {
      logger.error(e)
    }
  }

  openTwoFaModal = (setting: I2FASetting['type'], value: boolean) => {
    const settingFromMap = this._twoFAuthSettingsMap.get(setting)

    if (typeof settingFromMap?.value === 'undefined') return

    this._twoFaModalStore.openTwoFaModal({
      settings: [{ ...settingFromMap, value: value }],
      onSuccess: () => {
        runInAction(() => {
          settingFromMap.value = value
          this._twoFAuthSettingsMap.set(setting, settingFromMap)
          this.showSuccessMessage(setting, value)
        })
      },
    })
  }

  isDisabledSetting = (setting: I2FASetting['type']) => {
    return !!this._twoFAuthSettingsMap.get(setting)?.disabled
  }

  showSuccessMessage(setting: I2FASetting['type'], value: boolean) {
    const type2FA = setting === 'sms' ? setting.toUpperCase() : setting

    showToast({
      type: 'success',
      title: `Two factor authentication via ${setting === 'app' ? 'authenticator app' : type2FA} ${
        value ? 'enabled' : 'disabled'
      }`,
    })
  }

  openPhoneModal = (onConfirm: () => void) => {
    this._phoneModalOnConfirm = onConfirm
    this._phoneInputStore.reset()
    this.removeError('phone_number')

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: 'Phone number',
      ModalContent: () => <PhoneModal store={this} />,
      ModalActions: () => <PhoneModalActions store={this} />,
      showCloseButton: false,
      width: 360,
      onClose: () => {
        this._phoneInputStore.reset()
        this._phoneModalOnConfirm = null
        modalStore.removeModal(this._modalId)
      },
    })
  }

  onClosePhoneModal = (cancelled: boolean) => {
    this._phoneInputStore.reset()
    if (!cancelled) this._phoneModalOnConfirm?.()
    this._phoneModalOnConfirm = null
    modalStore.removeModal(this._modalId)
  }

  handlePhoneModalConfirm = async () => {
    if (!this._phoneInputStore.isValidNumber || this._loading) return

    try {
      runInAction(() => {
        this._loading = true
      })

      await UsersApi.updateUsersProfilePhone({
        number: this._phoneInputStore.fullUnformattedNumber,
      })

      await Promise.all([this._accountStore.profileStore.refreshUser(), this.getTwoFAuthSettings()])

      runInAction(() => {
        this._number = this._phoneInputStore.fullUnformattedNumber
        this.onClosePhoneModal(false)
      })

      showToast({
        type: 'success',
        title: 'Phone number updated',
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        if (e.errors.getError('number')) {
          this.setError('phone_number', e.errors.getError('number'))
        } else {
          showToast({
            type: 'error',
            title: 'Something went wrong. Please try again.',
          })
        }
      } else {
        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again.',
        })
      }
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  setError = (key: ISecurityFormFields, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: ISecurityFormFields) => {
    this._errorsMap.delete(key)
  }

  get sms() {
    return this._twoFAuthSettingsMap.get('sms')?.value ?? false
  }

  get email() {
    return this._twoFAuthSettingsMap.get('email')?.value ?? false
  }

  get app() {
    return this._twoFAuthSettingsMap.get('app')?.value ?? false
  }

  get disabled() {
    return this._loading || this._twoFAuthSettingsMap.size === 0
  }

  get isAllowedToSendSms() {
    const country = this._accountStore.user.country

    return (
      !country ||
      country === CountryEnum.US ||
      country === CountryEnum.CA ||
      country === CountryEnum.GB
    )
  }

  get initialLoading() {
    return this._initialLoading
  }

  get phoneInputStore() {
    return this._phoneInputStore
  }

  get errorsMap() {
    return this._errorsMap
  }

  get loading() {
    return this._loading
  }
}
